import React from 'react';
import { LINK_TYPE_OPTIONS } from '../../components/modals/TaskModal/Links/AddLinkModal/AddLinkModal';

export const formatDatesDmy = (dateString: string): string => {
  const months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  const d = new Date(dateString);
  const day = d.getDate();
  const daySuffix = getDaySuffix(day);
  const month = months[d.getMonth()];
  const year = d.getFullYear();

  return `${day}${daySuffix}, ${month} ${year}`;
};

const getDaySuffix = (day: number): string => {
  if (day >= 11 && day <= 13) {
    return 'th';
  }
  const lastDigit = day % 10;
  switch (lastDigit) {
    case 1:
      return 'st';
    case 2:
      return 'nd';
    case 3:
      return 'rd';
    default:
      return 'th';
  }
};
export const formatResponse = (response: string | unknown): unknown => {
  if (typeof response !== 'string') return response;

  // Split the response by lines and process each line
  const lines = response
    .split('\n')
    .map((line: string) => line.trim())
    .filter(Boolean);

  const formattedResponse: (string | string[])[] = [];
  let currentList: string[] | null = null;

  for (const line of lines) {
    if (/^\d+\.\s/.test(line)) {
      // Matches numbered lists (e.g., "1. Key: CS-720, Summary: ...")
      if (!currentList) {
        currentList = [];
        formattedResponse.push(currentList);
      }
      currentList.push(line);
    } else if (/^[-*]\s/.test(line)) {
      // Matches bullet lists (e.g., "- Key: CS-720, Summary: ...")
      if (!currentList) {
        currentList = [];
        formattedResponse.push(currentList);
      }
      currentList.push(line);
    } else {
      // Check for bold markers "**text**"
      const boldFormattedLine = line.replace(
        /\*\*(.+?)\*\*/g,
        '<strong>$1</strong>'
      );

      // Check for Markdown-style links "[text](url)"
      const linkFormattedLine = boldFormattedLine.replace(
        /\[([^\]]+)\]\((https?:\/\/[^\s]+)\)/g,
        (match, text, url) => `<a href="${url}" target="_blank">${text}</a>`
      );

      // Non-list line, process bold formatting and links if applicable
      if (currentList) {
        currentList = null; // End current list
      }
      formattedResponse.push(linkFormattedLine);
    }
  }

  return formattedResponse;
};

export const formatBoldText = (text: string): string => {
  // Replace **Text** with <strong>Text</strong>
  return text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
};

interface Issue {
  assignee?: { name?: string };
  createdAt?: string;
  issueUrl?: string;
  key?: string;
  status?: string;
  summary?: string;
  updatedAt?: string;
}

interface CompletionStatus {
  activePercentage?: number;
  completedPercentage?: number;
  dormantPercentage?: number;
  notStartedPercentage?: number;
  toDoPercentage?: number;
  unassignedPercentage?: number;
}

interface ExportData {
  completionStatus?: CompletionStatus;
  count?: number;
  progressPercentage?: number;
  projectKey?: string;
  refreshTime?: string;
  issues?: Issue[];
}

interface SelectedEpic {
  key: string;
}

export function exportToCSV(
  data: ExportData,
  selectedEpic: SelectedEpic
): void {
  // Define headers and rows for issues data with safe access
  const issueHeaders = [
    'Assignee',
    'Created At',
    'Issue URL',
    'Key',
    'Status',
    'Summary',
    'Updated At',
  ];

  const issues: Issue[] = Array.isArray(data.issues) ? data.issues : [];

  // Convert issues array into rows
  const issueRows = issues.map((issue: Issue) => [
    issue.assignee?.name || 'Unassigned',

    `"${new Date(issue?.createdAt ?? '').toLocaleString()}"`,
    issue?.issueUrl,
    issue?.key,
    issue?.status,
    issue?.summary,
    `"${new Date(issue?.updatedAt ?? '').toLocaleString()}"`,
  ]);

  // Combine main data and issues into a single CSV format
  // let csvContent = headers.join(',') + '\n';
  // rows.forEach((row) => {
  //   csvContent += row.join(',') + '\n';
  // });

  // Add a blank line before issues section and include issue headers
  let csvContent = issueHeaders.join(',') + '\n';
  issueRows.forEach((row) => {
    csvContent += row.join(',') + '\n';
  });

  // Create a Blob for download
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);

  // Trigger the download
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', `${selectedEpic.key}-issues.csv`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

export const formatMarkdownList = (text: string): string => {
  return text.replace(/(\d+)\.\s+/g, '\n$1. ');
};

interface ApiError {
  response?: {
    data?: {
      error?: string;
      message?: string | string[];
    };
  };
  message?: string;
}

export function getErrorMessage(
  error: ApiError | unknown,
  fallback: string = 'Something went wrong'
): string {
  const err = error as ApiError;
  if (err?.response?.data?.error) return err.response.data.error;
  if (Array.isArray(err?.response?.data?.message)) {
    return err.response.data.message.join(', ');
  }
  if (err?.response?.data?.message) return err.response.data.message;
  if (typeof err?.message === 'string') return err.message;
  return fallback;
}

export function getTimeAgoString(milliseconds: number): string {
  const seconds = Math.floor(milliseconds / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  const months = Math.floor(days / 30);
  const years = Math.floor(days / 365);

  if (years > 0) {
    return `${years} year${years !== 1 ? 's' : ''} ago`;
  } else if (months > 0) {
    return `${months} month${months !== 1 ? 's' : ''} ago`;
  } else if (days > 0) {
    return `${days} day${days !== 1 ? 's' : ''} ago`;
  } else if (hours > 0) {
    return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
  } else if (minutes > 0) {
    return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
  } else {
    return `${seconds} second${seconds !== 1 ? 's' : ''} ago`;
  }
}

export const formatText = (
  text: string | undefined | null
): { __html: string } => {
  return { __html: text?.replace(/\n/g, '<br>') || '' };
};

export const generateShortId = (): string => {
  return crypto.randomUUID().replace(/-/g, '').substring(0, 6).toUpperCase();
};

export const checkExistingPowerModeChat = (
  pickedProjects: any[],
  allChats: Record<string, any>
): string | null => {
  // Extract keys from pickedProjects array of objects
  const pickedProjectKeys = pickedProjects.map((project: any) => project.key);

  // Find chat with exactly the same projectKeys by iterating through allChats entries
  const matchingChatEntry = Object.entries(allChats).find(([_chatId, chat]) => {
    const chatProjectKeys = (chat.projectKeys || []).map(
      (project: any) => project.key
    );

    // Check if arrays have same length and same elements
    if (chatProjectKeys.length !== pickedProjectKeys.length) {
      return false;
    }

    // Check if both arrays contain the same elements (order doesn't matter)
    return pickedProjectKeys.every((key) => chatProjectKeys.includes(key));
  });

  const result = matchingChatEntry ? matchingChatEntry[0] : null;
  return result;
};

export const getFileExtension = (fileName: string): string => {
  return fileName.split('.').pop()?.toLowerCase() ?? '';
};

export const getStatusColorBg = (status: string | undefined | null): string => {
  const _status = status?.toLowerCase();
  switch (_status) {
    case 'completed':
    case 'done':
      return 'bg-[#4BCE97]';
    case 'active':
      return 'bg-[#AD98FE]';
    case 'pending':
    case 'in progress':
    case 'paused':
      return 'bg-[#F58E30]';
    case 'canceled':
      return 'bg-red-800';
    case 'on hold':
    case 'not started':
    case 'to do':
    case 'to-do':
      return 'bg-[#939393]';
    default:
      return 'bg-[#939393]';
  }
};

export const formatEnumText = (value: string | undefined | null): string => {
  if (!value) return '';
  return value
    .split('_') // split on underscores
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1)) // capitalize each word
    .join(' '); // join with spaces
};

export const toTitleCase = (str: string): string =>
  str
    .toLowerCase()
    .split(/[\s-_]+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

export const formatTimestamp = (
  timestamp: string | number | undefined | null
): string => {
  if (!timestamp) return 'None'; // or 'Unknown', 'N/A', etc.

  const date = new Date(timestamp);
  if (isNaN(date.getTime())) return 'Invalid date';

  const now = new Date();
  const diff = now.getTime() - date.getTime();

  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 0) return `${days} day${days > 1 ? 's' : ''} ago`;
  if (hours > 0) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
  if (minutes > 0) return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
  return 'Just now';
};

export const getLinkTypeIcon = (value: string): React.ReactNode | null => {
  const linkType = LINK_TYPE_OPTIONS.find(
    (option: { value: string; icon: React.ReactNode }) => option.value === value
  );
  return linkType ? linkType.icon : null;
};

export const getPriorityColor = (priority: string): string => {
  switch (priority) {
    case 'P0':
      return '#4DAD52';
    case 'P1':
      return '#9BA735';
    case 'P2':
      return '#954281';
    case 'P3':
      return '#225668';
    default:
      return '#939393';
  }
};
